import React from 'react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import Dog from '@rover/icons/svg/dog.svg';
import Grooming from '@rover/icons/svg/grooming.svg';
import Rebook from '@rover/icons/svg/rebook.svg';
import ReviewStarFill from '@rover/icons/svg/review-star-fill.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Flex, Text } from '@rover/kibble/core';
import { useStarColor } from '@rover/kibble/patterns/StarRating/StarRating.common';
import { formatCurrency } from '@rover/utilities/currency';

import { RATE_SIZE_INFO } from '../../../../GroomingServicePage/constants';

const ICON_SIZE = 16;

interface GroomingDetailsSectionProps {
  ratingsAverage?: string | null;
  reviewsCount: number;
  repeatClientCount: number;
  yearsOfExperience: string;
  isHosting: boolean;
  travelsToOwner: boolean;
  supportedPetSizes: string[];
  travelFee?: number | null;
  currencyCode?: string;
  locale?: Locale;
}

function getTravelingText(isHosting: boolean, travelsToOwner: boolean): string | null {
  if (isHosting && travelsToOwner) return 'Grooms at their home, travels to your home';
  if (isHosting) return 'Grooms at their home';
  if (travelsToOwner) return 'Travels to your home';
  return null;
}

function GroomingDetailsSection({
  ratingsAverage,
  reviewsCount,
  repeatClientCount,
  yearsOfExperience,
  isHosting,
  travelsToOwner,
  supportedPetSizes,
  travelFee,
  currencyCode = 'USD',
  locale = Locale.EnUs,
}: GroomingDetailsSectionProps): JSX.Element {
  const starFill = useStarColor();
  const rating = parseFloat(ratingsAverage ?? '') || 0;

  const baseTravelingText = getTravelingText(isHosting, travelsToOwner);
  const travelFeeText =
    travelsToOwner && travelFee
      ? ` (+${formatCurrency(travelFee, currencyCode, locale, { includeDecimal: false })})`
      : '';
  const travelingText = baseTravelingText && `${baseTravelingText}${travelFeeText}`;

  const showExperience = !!parseInt(yearsOfExperience, 10);

  const supportedSizeLabels = supportedPetSizes
    .map((size) => RATE_SIZE_INFO[size]?.label)
    .filter(Boolean);
  const supportedSizeText =
    supportedSizeLabels.length > 0 ? `Works with: ${supportedSizeLabels.join(', ')}` : '';

  return (
    <Flex flexDirection="column" gap="3x">
      {(reviewsCount > 0 || repeatClientCount > 0) && (
        <Flex gap="3x" alignItems="center">
          {reviewsCount > 0 && (
            <Flex gap="1x" alignItems="center">
              {rating > 0 ? (
                <>
                  <ReviewStarFill width={ICON_SIZE} height={ICON_SIZE} fill={starFill} />
                  <Text size="100">
                    <Text size="100" fontWeight="semibold">
                      {rating.toFixed(1)}
                    </Text>
                    {` (${reviewsCount})`}
                  </Text>
                </>
              ) : (
                <Text size="100">{`${reviewsCount} ${
                  reviewsCount === 1 ? 'review' : 'reviews'
                }`}</Text>
              )}
            </Flex>
          )}
          {repeatClientCount > 0 && (
            <Flex gap="1x" alignItems="center">
              <Rebook width={ICON_SIZE} height={ICON_SIZE} />
              <Text size="100">
                {`${repeatClientCount} ${
                  repeatClientCount === 1 ? 'repeat client' : 'repeat clients'
                }`}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
      {showExperience && (
        <Flex gap="1x" alignItems="center">
          <Trophy width={ICON_SIZE} height={ICON_SIZE} />
          <Text size="100">
            {parseInt(yearsOfExperience, 10) === 1
              ? `${yearsOfExperience} year of experience`
              : `${yearsOfExperience} years of experience`}
          </Text>
        </Flex>
      )}
      {supportedSizeText && (
        <Flex gap="1x" alignItems="center">
          <Dog width={ICON_SIZE} height={ICON_SIZE} />
          <Text size="100">{supportedSizeText}</Text>
        </Flex>
      )}
      {travelingText && (
        <Flex gap="1x" alignItems="center">
          <Grooming width={ICON_SIZE} height={ICON_SIZE} />
          <Text size="100">{travelingText}</Text>
        </Flex>
      )}
    </Flex>
  );
}

export default GroomingDetailsSection;
